import { useCallback, useEffect, useRef, useState } from 'react';
import { lipsyncManager } from '../lipsync';

// Continuous SpeechRecognition. Once started, keeps listening until stop() is called.
// - Fires onResult for each finalized utterance
// - Auto-restarts if the browser ends recognition on its own (silence timeout, etc.)
// - Exposes a paused state so the UI can suspend the mic while TTS is speaking
export function useSpeechRecognition({ onResult, lang = 'en-US' } = {}) {
  const recogRef = useRef(null);
  const wantOnRef = useRef(false);   // user intent: mic should be running
  const pausedRef = useRef(false);   // externally paused (e.g. while TTS is speaking)
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [supported, setSupported] = useState(true);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return;
    }
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = lang;

    r.onstart = () => setListening(true);

    r.onresult = (e) => {
      let finalText = '';
      let interimText = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t;
        else interimText += t;
      }
      setInterim(interimText);
      const trimmed = finalText.trim();
      if (trimmed && onResultRef.current) onResultRef.current(trimmed);
    };

    r.onerror = (e) => {
      // 'no-speech' and 'aborted' are normal; keep going if user still wants the mic on
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        wantOnRef.current = false;
        setListening(false);
      }
    };

    r.onend = () => {
      setInterim('');
      setListening(false);
      // Auto-restart if the user hasn't asked us to stop and we're not paused
      if (wantOnRef.current && !pausedRef.current) {
        try { r.start(); } catch (_) {}
      }
    };

    recogRef.current = r;
    return () => {
      wantOnRef.current = false;
      try { r.abort(); } catch (_) {}
    };
  }, [lang]);

  const start = useCallback(() => {
    if (!recogRef.current) return;
    wantOnRef.current = true;
    if (pausedRef.current) return; // will resume when unpaused
    try { recogRef.current.start(); } catch (_) {}
  }, []);

  const stop = useCallback(() => {
    if (!recogRef.current) return;
    wantOnRef.current = false;
    try { recogRef.current.stop(); } catch (_) {}
  }, []);

  const setPaused = useCallback((paused) => {
    pausedRef.current = paused;
    if (!recogRef.current) return;
    if (paused) {
      try { recogRef.current.stop(); } catch (_) {}
    } else if (wantOnRef.current) {
      try { recogRef.current.start(); } catch (_) {}
    }
  }, []);

  return { start, stop, setPaused, listening, interim, supported, wanted: () => wantOnRef.current };
}

// Natural TTS using OpenAI's `gpt-4o-mini-tts` (voice `nova`).
// Falls back to the browser's speechSynthesis if the network call fails.
export function useSpeechSynthesis() {
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef(null);
  const abortRef = useRef(null);
  const voicesRef = useRef([]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const load = () => (voicesRef.current = window.speechSynthesis.getVoices());
    load();
    window.speechSynthesis.onvoiceschanged = load;
  }, []);

  const stopAll = useCallback(() => {
    try { abortRef.current && abortRef.current.abort(); } catch (_) {}
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = '';
      } catch (_) {}
      audioRef.current = null;
    }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  }, []);

  const browserSpeak = useCallback((text) => {
    if (!window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.02;
    u.pitch = 1.2;
    const voices = voicesRef.current;
    const female =
      voices.find((v) => /samantha|victoria|karen|moira|tessa|amelie|google uk english female|zira/i.test(v.name)) ||
      voices.find((v) => v.lang && v.lang.startsWith('en'));
    if (female) u.voice = female;
    u.onstart = () => setSpeaking(true);
    u.onend = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
  }, []);

  const speak = useCallback(
    async (text) => {
      if (!text) return;
      stopAll();
      try {
        abortRef.current = new AbortController();
        const res = await fetch('/api/tts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, voice: 'nova' }),
          signal: abortRef.current.signal,
        });
        if (!res.ok) throw new Error('tts http ' + res.status);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.crossOrigin = 'anonymous';
        audioRef.current = audio;
        // Feed this audio element into the lip-sync analyser so the 3D avatar's
        // mouth follows the phonemes in real time.
        try { lipsyncManager.connectAudio(audio); } catch (_) {}
        audio.onplay = () => setSpeaking(true);
        audio.onended = () => {
          setSpeaking(false);
          URL.revokeObjectURL(url);
        };
        audio.onerror = () => {
          setSpeaking(false);
          URL.revokeObjectURL(url);
        };
        await audio.play();
      } catch (e) {
        if (e.name === 'AbortError') return;
        console.warn('OpenAI TTS failed, using browser voice:', e.message);
        browserSpeak(text);
      }
    },
    [stopAll, browserSpeak]
  );

  const cancel = useCallback(() => {
    stopAll();
    setSpeaking(false);
  }, [stopAll]);

  return { speak, cancel, speaking };
}
